import {
  LINKED_REQUIREMENTS_SECTION,
  groupLabelsInOrder,
  isLinkedRequirementsSection,
  sectionsForBuildItemType,
} from './functionalSpecSections';

function esc(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function slugify(name) {
  return String(name || 'spec')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48) || 'spec';
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

const STATUS_LABEL = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
};

function fieldBlock(label, value, mandatory = false) {
  const display = value?.trim()
    ? esc(value).split('\n').map((line) => `<p class="field-value">${line || '&nbsp;'}</p>`).join('')
    : '<p class="field-value muted">Not set</p>';
  return (
    `<div class="field">`
    + `<p class="field-label">${esc(label)}${mandatory ? ' *' : ''}</p>`
    + display
    + `</div>`
  );
}

/**
 * Build Word-compatible HTML for a functional specification.
 */
export function buildFunctionalSpecWordHtml({
  buildItemRef,
  buildItemTitle,
  buildItemType,
  status,
  linkedRequirements,
  templates,
  sectionByTemplateId,
}) {
  const applicable = sectionsForBuildItemType(templates, buildItemType);
  const groups = groupLabelsInOrder(applicable);
  const reqList = (linkedRequirements || []).map((r) => r.ref || r.title).filter(Boolean);

  const bodySections = groups.map((group) => {
    const rows = applicable.filter((t) => t.group_label === group);
    const inner = rows.map((t) => {
      if (isLinkedRequirementsSection(t)) {
        const val = reqList.length ? reqList.join(', ') : '';
        return fieldBlock(t.name, val, t.state === 'mandatory');
      }
      const row = sectionByTemplateId.get(t.id);
      return fieldBlock(t.name, row?.content || '', t.state === 'mandatory');
    }).join('');
    return `<h2>${esc(group)}</h2>${inner}`;
  }).join('');

  const title = buildItemTitle || buildItemRef || 'Functional specification';

  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<title>${esc(title)}</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
<style>
  @page { margin: 2cm; }
  body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #2c2c2a; line-height: 1.45; }
  h1 { font-size: 22pt; color: #3C3489; margin: 0 0 6pt; font-weight: 600; }
  h2 { font-size: 13pt; color: #534AB7; margin: 18pt 0 8pt; padding-bottom: 4pt; border-bottom: 1pt solid #d3d1c7; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5pt; }
  .subtitle { font-size: 12pt; color: #5f5e5a; margin: 0 0 14pt; }
  .meta { font-size: 10pt; color: #5f5e5a; margin: 0 0 18pt; }
  .meta p { margin: 2pt 0; }
  .field { margin: 0 0 10pt; }
  .field-label { font-size: 9pt; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4pt; color: #888780; margin: 0 0 3pt; }
  .field-value { margin: 0 0 4pt; }
  .muted { color: #B4B2A9; font-style: italic; }
  .footer { margin-top: 24pt; padding-top: 10pt; border-top: 0.5pt solid #d3d1c7; font-size: 9pt; color: #888780; }
</style>
</head>
<body>
  <h1>${esc(title)}</h1>
  <p class="subtitle">Functional Specification · ${esc(buildItemRef || '—')} · ${esc(buildItemType || '—')}</p>
  <div class="meta">
    <p>Status: ${esc(STATUS_LABEL[status] || status)}</p>
    <p>Linked requirements: ${esc(reqList.join(', ') || 'None')}</p>
    <p>Exported: ${esc(formatDate(new Date().toISOString()))}</p>
  </div>
  ${bodySections}
  <div class="footer">Generated from Transformax · Functional Specification · * = mandatory section</div>
</body>
</html>`;
}

export function downloadFunctionalSpecWord(params) {
  const html = buildFunctionalSpecWordHtml(params);
  const ref = params.buildItemRef || 'spec';
  const filename = `functional-spec-${slugify(ref)}-${slugify(params.buildItemTitle)}.doc`;
  const blob = new Blob(['\ufeff', html], { type: 'application/msword;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return filename;
}

export { LINKED_REQUIREMENTS_SECTION };
